import type { FC } from 'react';
import { useMemo, useState } from 'react';

import { useRouter } from 'next/router';

import { TFilterSections } from '@pra-vendas-themes/interfaces/ILayoutFilter';
import LayoutFilter from '@pra-vendas-themes/default/components/LayoutFilter';

import { useSiteSettings } from 'hooks/useSiteSettings';
import { useProductsFilters } from 'hooks/useProductsFilters';
import { useProducts } from 'hooks/useProducts';

import { formatAttributesFilters } from 'utils/formatAttributesFilters';

import { ICategory } from 'types/ICategory';

import { TitleWithSort } from 'components/TitleWithSort';
import { ProductsList } from 'components/ProductsList';
import { Body } from 'components/Body';
import { BannerCategory } from 'components/BannerCategory';
import { Alert } from 'components/Alert';
import { useProductsPaginated } from 'hooks/useProductsPaginated';

const Category: FC = () => {
  const router = useRouter();
  const [sortBy, setSortBy] = useState<string>();
  const { categories } = useSiteSettings();
  const { query } = router;

  const categoriesFound: { category?: ICategory; categoriesId: string } =
    useMemo(() => {
      let subcategory;

      const category = categories?.find(
        (cat) =>
          cat.tag === query?.categoria ||
          cat.subcategories?.find((subcat) => subcat.tag === query?.categoria)
      );

      if (category?.tag !== query?.categoria)
        subcategory = category?.subcategories?.find(
          (subcat) => subcat.tag === query?.categoria
        );

      if (subcategory)
        return {
          category: subcategory,
          categoriesId: subcategory._id,
        };

      return {
        category,
        categoriesId: [
          category?._id,
          ...(category?.subcategories?.map((sub) => sub._id) || []),
        ].join('|'),
      };
    }, [categories, query]);

  const {
    filters: productsFilters,
    selectedFilters,
    selectFilter,
  } = useProductsFilters({
    params: {
      categories: categoriesFound.categoriesId,
    },
  });

  const formattedAttributesFilters = useMemo(
    () => formatAttributesFilters(selectedFilters),
    [selectedFilters]
  );

  const { productsData, hasMore, setPage, isLoading } = useProductsPaginated({
    params: {
      categories: categoriesFound.categoriesId,
      sortBy,
      attributes: formattedAttributesFilters,
    },
  });

  const handleChangeSortBy = (sortSelected?: string): void => {
    if (sortSelected) {
      setSortBy(sortSelected);
    } else {
      setSortBy(undefined);
    }
  };

  const quantityProducts = productsData?.length || 0;

  const categoriesOptions = useMemo(
    () =>
      categories
        ?.filter((cat) => cat._id !== categoriesFound.category?._id)
        .map((cat) => ({
          title: cat.name,
          href: `/categorias/${cat.tag}`,
        })),
    [categories, categoriesFound]
  );

  const subCategoriesOptions = useMemo(
    () =>
      categoriesFound.category?.subcategories?.map((sub) => ({
        title: sub.name,
        href: `/categorias/${sub.tag}`,
      })),
    [categoriesFound]
  );

  const formattedFilters = useMemo(() => {
    const filters: TFilterSections[] =
      categoriesOptions?.length || subCategoriesOptions?.length
        ? [
            {
              title: 'Subcategorias',
              items: subCategoriesOptions,
            },
            {
              title: 'Categorias',
              items: categoriesOptions,
            },
          ]
        : [];

    productsFilters.forEach((productsFilter) => {
      filters.push({
        title: productsFilter.label,
        items: productsFilter.options.map((option) => ({
          title: option.label,
          onClick: () => selectFilter(productsFilter._id, option._id),
          isSelected: selectedFilters?.[productsFilter._id]?.includes(
            option._id
          ),
        })),
      });
    });

    return filters;
  }, [
    categoriesOptions,
    productsFilters,
    selectFilter,
    selectedFilters,
    subCategoriesOptions,
  ]);

  return (
    <>
      <BannerCategory banners={categoriesFound.category?.banners} />
      <Body hasSpacingY>
        <TitleWithSort
          title={categoriesFound.category?.name || ''}
          onChangeSortBy={handleChangeSortBy}
          subtitle={
            quantityProducts === 1
              ? '1 produto'
              : `${quantityProducts} produtos`
          }
        />
        <LayoutFilter filters={formattedFilters}>
          <ProductsList
            products={productsData}
            shimmerEffectQuantity={6}
            isLoading={isLoading}
            initialColumnItemsCount={3}
            setPage={setPage}
            hasMore={hasMore}
          />

          {productsData && !productsData.length && !isLoading && (
            <Alert color="warning">
              Não existem produtos cadastrados nessa categoria.
            </Alert>
          )}
        </LayoutFilter>
      </Body>
    </>
  );
};

export default Category;
